import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Buildings, EnvelopeSimple, Sparkle, Bug, ArrowRight, Broom, Lightning, MagicWand } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

function pctColor(pct, inverted = false) {
  // inverted=true → low is good (e.g. junk %)
  if (pct === null || pct === undefined) return 'text-zinc-400';
  if (inverted) {
    if (pct < 1) return 'text-emerald-600';
    if (pct < 5) return 'text-amber-600';
    return 'text-red-600';
  }
  if (pct >= 70) return 'text-emerald-600';
  if (pct >= 30) return 'text-amber-600';
  return 'text-red-600';
}

function pctBar(pct, inverted = false) {
  if (pct === null || pct === undefined) return 'bg-zinc-200';
  if (inverted) {
    if (pct < 1) return 'bg-emerald-500';
    if (pct < 5) return 'bg-amber-500';
    return 'bg-red-500';
  }
  if (pct >= 70) return 'bg-emerald-500';
  if (pct >= 30) return 'bg-amber-500';
  return 'bg-red-500';
}

/**
 * Data Quality Health Check — per-profile thermometer.
 * Shows total facilities, % geclassificeerd, % met e-mail, % junk-contacten.
 * Color-coded so the user can spot weak profiles in one glance.
 */
export default function DataQualityWidget() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState({});  // { 'slug:action': { job_id, statusUrl, processed, total, status } }

  const fetchData = useCallback(() => {
    return fetch(`${API}/api/profiles/data-quality`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Poll any in-progress jobs every 2.5s
  useEffect(() => {
    const inFlight = Object.entries(jobs).filter(([, j]) => j.status === 'running' && j.statusUrl);
    if (inFlight.length === 0) return;
    const id = setInterval(async () => {
      for (const [key, j] of inFlight) {
        try {
          const r = await fetch(`${API}${j.statusUrl}`, { credentials: 'include' });
          if (!r.ok) continue;
          const s = await r.json();
          setJobs(prev => ({
            ...prev,
            [key]: { ...prev[key], processed: s.processed || 0, total: s.total || 0, status: s.status || 'running' },
          }));
          if (s.status === 'done' || s.status === 'error') {
            // Refresh stats and auto-clear job after 4s so user sees the ✓
            fetchData();
            setTimeout(() => {
              setJobs(prev => {
                const next = { ...prev };
                delete next[key];
                return next;
              });
            }, 4000);
          }
        } catch { /* keep polling */ }
      }
    }, 2500);
    return () => clearInterval(id);
  }, [jobs, fetchData]);

  const runAction = async (slug, action, label) => {
    const key = `${slug}:${action}`;
    setJobs(prev => ({ ...prev, [key]: { status: 'starting', processed: 0, total: 0 } }));
    try {
      const sw = await fetch(`${API}/api/profiles/switch`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug }),
      });
      if (!sw.ok) throw new Error('Profielwissel mislukt');

      let endpoint = '';
      let statusUrlBase = '';
      if (action === 'junk') {
        endpoint = '/api/contacts-email-rescue/purge-junk';
      } else if (action === 'classify') {
        endpoint = '/api/facilities/classify-dimensions?limit=200';
        statusUrlBase = '/api/facilities/classify-dimensions/status';
      } else if (action === 'rescue') {
        endpoint = '/api/contacts-email-rescue/apply-patterns';
        statusUrlBase = '/api/contacts-email-rescue/status';
      }

      const r = await fetch(`${API}${endpoint}`, { method: 'POST', credentials: 'include' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || `${label} mislukt`);

      if (action === 'junk') {
        // Synchronous result — display inline confirmation
        setJobs(prev => ({
          ...prev,
          [key]: { status: 'done', processed: j.deleted || 0, total: j.deleted || 0, label_done: `${j.deleted || 0} verwijderd` },
        }));
        setTimeout(() => {
          setJobs(prev => { const n = { ...prev }; delete n[key]; return n; });
        }, 4000);
        await fetchData();
      } else {
        setJobs(prev => ({
          ...prev,
          [key]: { job_id: j.job_id, statusUrl: `${statusUrlBase}/${j.job_id}`, processed: 0, total: 0, status: 'running' },
        }));
      }
    } catch (e) {
      window.alert('Fout: ' + e.message);
      setJobs(prev => { const n = { ...prev }; delete n[key]; return n; });
    }
  };

  if (loading) {
    return (
      <div className="mb-8 border border-zinc-200 bg-white p-4" data-testid="data-quality-widget-loading">
        <div className="h-4 w-32 bg-zinc-100 animate-pulse rounded" />
      </div>
    );
  }
  if (!data?.profiles?.length) return null;

  return (
    <div className="mb-8" data-testid="data-quality-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkle size={16} weight="fill" className="text-indigo-600" />
          <h2 className="font-heading text-base font-semibold">Data-kwaliteit per profiel</h2>
        </div>
        <button
          onClick={() => navigate('/facilities')}
          className="text-xs font-medium text-[#0055FF] hover:underline flex items-center gap-1"
          data-testid="dq-view-facilities-btn"
        >
          Naar facilities <ArrowRight size={12} />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.profiles.map(p => (
          <div
            key={p.slug}
            data-testid={`dq-card-${p.slug}`}
            className="border border-zinc-200 bg-white p-4 hover:border-zinc-300 transition-colors"
            style={{ borderLeft: `4px solid ${p.color || '#0F172A'}` }}
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-heading text-sm font-bold">{p.name}</div>
                <div className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5">
                  <Buildings size={11} />
                  <span className="font-data">{p.total_facilities.toLocaleString('nl-NL')}</span> faciliteiten
                </div>
              </div>
            </div>

            {/* % Verified email */}
            <Metric
              icon={<EnvelopeSimple size={11} />}
              label="Met e-mailadres"
              pct={p.with_email_pct}
              count={p.with_email_count}
              total={p.total_facilities}
              testId={`dq-${p.slug}-email`}
            />

            {/* % Geclassificeerd (alleen voor V2 profielen) */}
            {p.has_dimensions ? (
              <Metric
                icon={<Sparkle size={11} />}
                label="Kenmerken AI-getagd"
                pct={p.classified_pct}
                count={p.classified_count}
                total={p.total_facilities}
                testId={`dq-${p.slug}-classified`}
              />
            ) : (
              <div className="text-[11px] text-zinc-400 italic mt-2" data-testid={`dq-${p.slug}-no-dims`}>
                Geen profiel-kenmerken geconfigureerd
              </div>
            )}

            {/* % Junk (inverted: low = good) */}
            <Metric
              icon={<Bug size={11} />}
              label="Junk contacten"
              pct={p.junk_pct}
              count={p.junk_contacts}
              total={p.total_contacts}
              inverted
              testId={`dq-${p.slug}-junk`}
            />

            {/* 1-klik herstelacties — alleen wanneer score in amber/rood */}
            <ActionButtons profile={p} jobs={jobs} onRun={runAction} />
          </div>
        ))}
      </div>
    </div>
  );
}

function Metric({ icon, label, pct, count, total, inverted = false, testId }) {
  if (pct === null || pct === undefined) return null;
  return (
    <div className="mt-2" data-testid={testId}>
      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className="text-zinc-600 flex items-center gap-1">{icon}{label}</span>
        <span className={`font-data font-bold ${pctColor(pct, inverted)}`}>
          {pct}%
          <span className="text-zinc-400 font-normal ml-1">
            ({count.toLocaleString('nl-NL')}/{total.toLocaleString('nl-NL')})
          </span>
        </span>
      </div>
      <div className="h-1.5 bg-zinc-100 rounded-sm overflow-hidden">
        <div
          className={`h-full ${pctBar(pct, inverted)} transition-all`}
          style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
        />
      </div>
    </div>
  );
}


function ActionButtons({ profile: p, jobs, onRun }) {
  const actions = [];

  // Junk schoonmaken (>=5% rood) — niet voor profielen zonder contacten
  if (p.total_contacts > 0 && p.junk_pct >= 5) {
    actions.push({
      key: 'junk',
      label: 'Schoon junk op',
      Icon: Broom,
      color: 'bg-red-600 hover:bg-red-700',
      confirm: `${p.junk_contacts} junk-contacten verwijderen voor ${p.name}?`,
    });
  }

  // Email-rescue (<30% met e-mail = rood) — alleen als er facilities ZIJN
  if (p.total_facilities > 0 && p.with_email_pct < 30) {
    actions.push({
      key: 'rescue',
      label: 'Start email-rescue',
      Icon: MagicWand,
      color: 'bg-indigo-600 hover:bg-indigo-700',
      confirm: `Start patroon-gebaseerde email-rescue voor ${p.name}? Dit kan enkele minuten duren.`,
    });
  }

  // Classify ontbrekende (only V2 profielen + < 70% AND niet-volledig)
  if (p.has_dimensions && p.classified_pct !== null && p.classified_pct < 70
      && p.total_facilities - p.classified_count > 0) {
    const remaining = p.total_facilities - p.classified_count;
    actions.push({
      key: 'classify',
      label: `Classificeer ${Math.min(remaining, 200)}`,
      Icon: Lightning,
      color: 'bg-amber-600 hover:bg-amber-700',
      confirm: `Klassificeer max 200 ontbrekende facilities voor ${p.name} met GPT-5.2?`,
    });
  }

  if (!actions.length) return null;

  return (
    <div className="mt-3 pt-3 border-t border-zinc-100 flex flex-col gap-1.5" data-testid={`dq-${p.slug}-actions`}>
      {actions.map(a => {
        const k = `${p.slug}:${a.key}`;
        const job = jobs[k];
        return (
          <ActionRow
            key={a.key}
            slug={p.slug}
            action={a}
            job={job}
            onClick={() => {
              if (window.confirm(a.confirm)) onRun(p.slug, a.key, a.label);
            }}
          />
        );
      })}
    </div>
  );
}

function ActionRow({ slug, action: a, job, onClick }) {
  if (!job) {
    return (
      <button
        onClick={onClick}
        data-testid={`dq-${slug}-action-${a.key}`}
        className={`text-[11px] font-medium text-white px-2.5 py-1 rounded-sm transition-colors flex items-center gap-1.5 self-start ${a.color}`}
      >
        <a.Icon size={11} weight="bold" />
        {a.label}
      </button>
    );
  }

  // job exists → show progress
  const pct = job.total > 0 ? Math.round((job.processed / job.total) * 100) : 0;
  const isDone = job.status === 'done';
  const isErr = job.status === 'error';
  const isStarting = job.status === 'starting';

  return (
    <div className="flex flex-col gap-1" data-testid={`dq-${slug}-job-${a.key}`}>
      <div className="flex items-center justify-between text-[11px]">
        <span className="flex items-center gap-1.5 font-medium text-zinc-700">
          <a.Icon size={11} weight="bold" className={isDone ? 'text-emerald-600' : isErr ? 'text-red-600' : 'text-indigo-600'} />
          {isStarting ? `${a.label} — starten...` :
           isDone ? (a.key === 'junk' ? `✓ ${job.label_done || a.label}` : `✓ ${a.label} klaar`) :
           isErr ? `✕ ${a.label} mislukt` :
           `${a.label} — ${job.processed || 0}${job.total ? `/${job.total}` : ''}`}
        </span>
        {!isStarting && !isDone && !isErr && job.total > 0 && (
          <span className="font-data text-zinc-500">{pct}%</span>
        )}
      </div>
      <div className="h-1 bg-zinc-100 rounded-sm overflow-hidden" data-testid={`dq-${slug}-progress-${a.key}`}>
        <div
          className={`h-full transition-all duration-500 ${isDone ? 'bg-emerald-500 w-full' : isErr ? 'bg-red-500 w-full' : isStarting ? 'bg-indigo-300 animate-pulse w-1/4' : 'bg-indigo-500'}`}
          style={!isDone && !isErr && !isStarting ? { width: `${Math.max(2, pct)}%` } : {}}
        />
      </div>
    </div>
  );
}
